export const TOKEN_KEY = 'rfis';
export const ADMIN_USER_STORAGE_KEY = 'rfis_admin_user';
export const TAB_REGISTRY_KEY = 'rfis_tab_registry';
export const TAB_ID_KEY = 'rfis_tab_id';
export const BROWSER_SESSION_KEY = 'rfis_browser_session';
export const BROWSER_SESSION_TOUCH_KEY = 'rfis_browser_session_touch';
export const LAST_ACTIVITY_KEY = 'rfis_last_activity';
export const SESSION_WARNING_EXPIRES_KEY = 'rfis_session_warning_expires';

/** Persist admin profile returned from login for sidebar / account UI. */
export function setStoredAdminUser(user) {
  if (user) {
    localStorage.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify(user));
  }
}

export function clearStoredAdminUser() {
  localStorage.removeItem(ADMIN_USER_STORAGE_KEY);
}

export function getStoredAdminUser() {
  try {
    const raw = localStorage.getItem(ADMIN_USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
}

/** Legacy custom header — may be dropped by nginx/Azure if underscores are disabled. */
export const AUTH_TOKEN_HEADER = "rf_token_header123_k";

/**
 * Headers for authenticated API calls.
 * Always include Authorization: Bearer (proxy-safe) plus the legacy custom header.
 */
export function getAuthHeaders(extra = {}) {
  const token = getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    ...extra,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers[AUTH_TOKEN_HEADER] = token;
    // Hyphenated alias survives reverse proxies that strip underscore headers.
    headers["rf-token-header123-k"] = token;
  }

  return headers;
}

/** Remove all authentication and session-related values from browser storage. */
export function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEY);
  clearStoredAdminUser();
  localStorage.removeItem(TAB_REGISTRY_KEY);
  localStorage.removeItem(BROWSER_SESSION_KEY);
  localStorage.removeItem(BROWSER_SESSION_TOUCH_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SESSION_WARNING_EXPIRES_KEY);
}

export function decodeJwtPayload(token) {
  try {
    if (!token) return null;
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return null;

    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token = getAuthToken()) {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 <= Date.now();
}

/**
 * Unified profile for UI — merges persisted login payload with JWT claims.
 * NOTE: UI-only; server enforces authorization.
 */
export function getAdminProfile() {
  const stored = getStoredAdminUser();
  const jwt = getCurrentAdminUser();
  if (!stored && !jwt) return null;

  const email = stored?.email || jwt?.email || '';
  const fullName =
    stored?.fullName ||
    stored?.name ||
    (email ? email.split('@')[0] : 'Admin User');
  const username = stored?.username || '';
  const role = stored?.role || jwt?.role || 'Editor';

  return {
    id: stored?.id ?? jwt?.adminPanelUserId ?? null,
    fullName,
    username,
    email,
    role,
    status: stored?.status || 'Active',
  };
}

/**
 * Decodes the stored JWT token to read the current admin user's role.
 * NOTE: This is for UI-only decisions (show/hide, redirects).
 * All real security enforcement is done server-side.
 */
export function getCurrentAdminUser() {
  return decodeJwtPayload(getAuthToken());
}

/** Returns true if the current logged-in admin has the "admin" role. */
export function isAdminRole() {
  const user = getCurrentAdminUser();
  return user?.role?.toLowerCase() === 'admin';
}

export function isAuthenticated() {
  const token = getAuthToken();
  if (!token) return false;
  if (isTokenExpired(token)) {
    clearAuthStorage();
    return false;
  }
  return true;
}
